import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import {
  getConsentSnapshot,
  hasAdsMeasurementConsent,
  hasAnalyticsConsent,
  subscribeToConsentChanges,
  type ConsentSnapshot,
} from '@services/consent';
import {
  canQueueGoogleAdsEvent,
  getGoogleTagLoadState,
  isTemporaryAdvancedConsentModeActive,
  recoverGoogleTagLoaderForEvent,
} from '@services/googleTags';
import {
  hashEnhancedConversionEmail,
  hashGoogleAdsTransactionId,
  isSha256EmailAddress,
} from '@services/enhancedConversions';
import { getUserEmail } from '@services/token';

let appInsights: ApplicationInsights | undefined;
let appInsightsInitPromise: Promise<void> | null = null;
let analyticsConsentActive = false;
let unsubscribeFromConsent: (() => void) | null = null;
let stopWebVitalsObservation: (() => void) | null = null;

// Ad/analytics beacon endpoints excluded from AJAX dependency tracking (see the telemetry
// initializer below). Matched as substrings against the dependency's target + name.
const AD_ANALYTICS_URL_FRAGMENTS = [
  'google-analytics.com',
  'analytics.google.com',
  'googletagmanager.com',
  'googleadservices.com',
  'googlesyndication.com',
  'doubleclick.net',
  'google.com/pagead',
  'google.com/ccm',
  'connect.facebook.net',
  'facebook.com/tr',
  '/cdn-cgi/rum',
  '/safgtw/',
];

export function shouldExcludeOptionalTelemetryDependency(url: string): boolean {
  const normalized = url.toLowerCase();
  return AD_ANALYTICS_URL_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

async function ensureAppInsightsLoaded(): Promise<void> {
  if (!hasAnalyticsConsent() || import.meta.env.VITE_DISABLE_OPTIONAL_TELEMETRY === 'true') {
    return;
  }

  if (appInsights || appInsightsInitPromise) {
    return appInsightsInitPromise ?? Promise.resolve();
  }

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string | undefined;
  if (!connectionString) {
    return;
  }

  appInsightsInitPromise = import('@microsoft/applicationinsights-web')
    .then((mod) => {
      // Consent may have been withdrawn while the optional SDK chunk was loading.
      if (!hasAnalyticsConsent() || !analyticsConsentActive) {
        return;
      }
      const { ApplicationInsights: InsightsCtor } = mod as typeof import('@microsoft/applicationinsights-web');
      const instance = new InsightsCtor({
        config: {
          connectionString,
          disableAjaxTracking: false,
          disableFetchTracking: false,
          enableAutoRouteTracking: true,
          disablePageUnloadEvents: ['unload'],
          // Analytics starts only after consent and remains memory-only. This prevents queued or
          // identified telemetry from resurfacing after withdrawal/regrant; legacy SDK storage is
          // still removed by the centralized withdrawal cleanup.
          disableCookiesUsage: true,
          isStorageUseDisabled: true,
          enableSessionStorageBuffer: false,
        },
      });

      // Drop auto-collected AJAX dependencies aimed at ad/analytics beacons. Ad blockers kill those
      // requests, which otherwise pollute the failed-dependency view with false failures (observed:
      // GA / DoubleClick / AdServices beacons blocked client-side). First-party API/storage calls and
      // functional third parties (Stripe, Turnstile) keep flowing.
      instance.addTelemetryInitializer((item) => {
        if (item.baseType !== 'RemoteDependencyData') {
          return true;
        }
        const data = item.baseData as { name?: string; target?: string } | undefined;
        const url = `${data?.target ?? ''} ${data?.name ?? ''}`;
        return !shouldExcludeOptionalTelemetryDependency(url);
      });

      instance.loadAppInsights();
      // The SDK does not record the initial page view by itself (enableAutoRouteTracking only fires
      // on subsequent route changes), so track the landing view explicitly.
      instance.trackPageView();
      appInsights = instance;
      initWebVitals(instance);
    })
    .catch(() => {
      // Telemetry is non-critical; ignore failures.
    })
    .finally(() => {
      appInsightsInitPromise = null;
    });

  return appInsightsInitPromise;
}

/**
 * Core Web Vitals field measurement (LCP / CLS / INP). The App Insights SDK doesn't collect these,
 * which left us blind to what Chrome UX Report sees - Search Console flags an LCP regression and we
 * have no first-party data to confirm or deny it. Observes the native performance entries and sends
 * a single 'web-vitals' custom event per page load, when the page is first hidden (the values are
 * final by then; matches how CrUX measures). Initial navigation only - SPA route changes don't get
 * a field LCP, same as CrUX. Query: AppEvents | where Name == 'web-vitals' (values in Measurements).
 * Best-effort: feature-detected and fully wrapped so it can never break the app.
 */
function initWebVitals(instance: ApplicationInsights): void {
  try {
    if (typeof PerformanceObserver === 'undefined' || typeof document === 'undefined') {
      return;
    }
    stopWebVitalsObservation?.();
    let active = true;
    const observers: PerformanceObserver[] = [];
    const supported: readonly string[] = PerformanceObserver.supportedEntryTypes ?? [];

    let lcp: number | undefined;

    // CLS per the session-window definition: shifts less than 1s apart (and within a 5s window)
    // accumulate; the reported CLS is the worst window.
    let cls = 0;
    let clsWindow = 0;
    let clsWindowStart = 0;
    let clsLastShiftAt = 0;

    // INP approximation: worst-case interaction latency (exact INP for pages with <50 interactions,
    // which is virtually all of ours). Entries below the 40ms observer threshold are "good" anyway.
    const interactionMax = new Map<number, number>();

    if (supported.includes('largest-contentful-paint')) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) {
          lcp = last.startTime;
        }
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(observer);
    }

    if (supported.includes('layout-shift')) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          if (entry.hadRecentInput) {
            continue;
          }
          if (clsWindow > 0 && entry.startTime - clsLastShiftAt < 1000 && entry.startTime - clsWindowStart < 5000) {
            clsWindow += entry.value;
          } else {
            clsWindow = entry.value;
            clsWindowStart = entry.startTime;
          }
          clsLastShiftAt = entry.startTime;
          if (clsWindow > cls) {
            cls = clsWindow;
          }
        }
      });
      observer.observe({ type: 'layout-shift', buffered: true });
      observers.push(observer);
    }

    if (supported.includes('event')) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          if (!entry.interactionId) {
            continue;
          }
          const current = interactionMax.get(entry.interactionId) ?? 0;
          if (entry.duration > current) {
            interactionMax.set(entry.interactionId, entry.duration);
          }
        }
      });
      observer.observe({ type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
      observers.push(observer);
    }

    const landingPage = window.location.pathname;
    let reported = false;
    const report = () => {
      if (
        reported ||
        !active ||
        !hasAnalyticsConsent() ||
        appInsights !== instance
      ) {
        return;
      }
      if (lcp === undefined && cls === 0 && interactionMax.size === 0) {
        return;
      }
      reported = true;

      const measurements: Record<string, number> = {};
      if (lcp !== undefined) {
        measurements.lcp = Math.round(lcp);
      }
      measurements.cls = Math.round(cls * 1000) / 1000;
      if (interactionMax.size > 0) {
        measurements.inp = Math.round(Math.max(...interactionMax.values()));
      }

      instance.trackEvent({
        name: 'web-vitals',
        properties: { page: landingPage },
        measurements,
      });
      instance.flush();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        report();
      }
    };
    // pagehide covers browsers/paths where visibilitychange doesn't fire on close.
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', report);

    stopWebVitalsObservation = () => {
      if (!active) {
        return;
      }
      active = false;
      observers.forEach((observer) => observer.disconnect());
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', report);
      interactionMax.clear();
      stopWebVitalsObservation = null;
    };
  } catch {
    // Telemetry is non-critical; never let vitals collection break the app.
  }
}

export function initTelemetry(): void {
  const applyConsent = (snapshot: ConsentSnapshot) => {
    const shouldEnable = Boolean(
      !import.meta.env.DEV &&
        import.meta.env.VITE_DISABLE_OPTIONAL_TELEMETRY !== 'true' &&
        import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING &&
        snapshot.hasDecision &&
        snapshot.preferences.analytics,
    );

    analyticsConsentActive = shouldEnable;
    if (shouldEnable) {
      void ensureAppInsightsLoaded();
      return;
    }

    if (appInsights) {
      stopWebVitalsObservation?.();
      // Stop collection before unloading so revocation cannot flush a new telemetry item.
      appInsights.config.disableTelemetry = true;
      try {
        appInsights.unload(false);
      } catch {
        // Telemetry is non-critical; consent enforcement remains fail-closed via the flag above.
      }
      appInsights = undefined;
    }
  };

  applyConsent(getConsentSnapshot());
  if (!unsubscribeFromConsent) {
    unsubscribeFromConsent = subscribeToConsentChanges(applyConsent);
  }
}

export function trackPageView(name?: string): void {
  if (hasAnalyticsConsent()) {
    appInsights?.trackPageView({ name });
  }
}

/**
 * Reports a product event (e.g. 'upgrade-prompt-shown') to App Insights. Query with
 * AppEvents | where Name == '<name>'. Best-effort and never throws into the caller.
 */
export function trackEvent(name: string, properties?: Record<string, string>): void {
  try {
    if (hasAnalyticsConsent()) {
      appInsights?.trackEvent({ name, properties });
    }
  } catch {
    // Telemetry is non-critical.
  }
}

/**
 * Reports a handled/caught error to App Insights so client-side failures (e.g. a download that
 * fetched fine but failed to stream/decrypt) are visible in telemetry. The SDK only auto-collects
 * *uncaught* errors, so failures swallowed by try/catch have to be reported explicitly. Best-effort:
 * if telemetry isn't initialised yet this is a no-op, and it never throws into the caller.
 */
export function trackException(error: unknown, properties?: Record<string, string>): void {
  try {
    if (!appInsights || !hasAnalyticsConsent()) {
      return;
    }
    const exception = error instanceof Error
      ? error
      : new Error(typeof error === 'string' ? error : 'Unknown error');
    appInsights.trackException({ exception }, properties);
  } catch {
    // Telemetry is non-critical; never let reporting break the caller.
  }
}

export type ConversionTrackingResult = 'queued' | 'denied' | 'unavailable';
type GoogleAdsConversionGoal = 'signup' | 'file_upload' | 'pwa_install' | 'subscription';

const GOOGLE_ADS_EVENT_CALLBACK_TIMEOUT_MS = 2_000;
const BYTES_PER_MIB = 1024 * 1024;
const FILE_UPLOAD_100_MIB = 100 * BYTES_PER_MIB;
const FILE_UPLOAD_500_MIB = 500 * BYTES_PER_MIB;
const FILE_UPLOAD_2_GIB = 2 * 1024 * BYTES_PER_MIB;

interface EnhancedConversionOptions {
  sha256EmailAddress?: string | null;
}

async function queueGoogleAdsConversion(
  goal: GoogleAdsConversionGoal,
  sendTo: string,
  parameters: Record<string, string | number> = {},
  options: EnhancedConversionOptions = {},
): Promise<ConversionTrackingResult> {
  recoverGoogleTagLoaderForEvent();
  const adsMeasurementInitiallyGranted = hasAdsMeasurementConsent();
  if (!canQueueGoogleAdsEvent() || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    const result =
      !adsMeasurementInitiallyGranted && !isTemporaryAdvancedConsentModeActive()
        ? 'denied'
        : 'unavailable';
    trackEvent('google-ads-conversion-command', {
      goal,
      result,
      tagLoadState: getGoogleTagLoadState(),
    });
    return result;
  }

  let sha256EmailAddress: string | null = null;
  if (adsMeasurementInitiallyGranted) {
    sha256EmailAddress = isSha256EmailAddress(options.sha256EmailAddress)
      ? options.sha256EmailAddress
      : await hashEnhancedConversionEmail(getUserEmail() ?? '');
  }

  // Web Crypto is asynchronous, so re-check both the loader and the user's choice before queuing
  // anything. If consent was withdrawn, Advanced Consent Mode may still accept a base cookieless
  // event, but enhanced identity must be discarded immediately.
  const adsMeasurementGranted = hasAdsMeasurementConsent();
  if (!canQueueGoogleAdsEvent() || typeof window.gtag !== 'function') {
    const result =
      !adsMeasurementGranted && !isTemporaryAdvancedConsentModeActive()
        ? 'denied'
        : 'unavailable';
    trackEvent('google-ads-conversion-command', {
      goal,
      result,
      tagLoadState: getGoogleTagLoadState(),
    });
    return result;
  }

  // Advanced Consent Mode may queue a cookieless conversion command while ad storage and ad user
  // data remain denied. Only aggregate conversion fields are allowed on that path; stable IDs and
  // any future caller-supplied identifiers remain restricted to a genuine first-party grant.
  const consentSafeParameters = adsMeasurementGranted
    ? parameters
    : Object.fromEntries(
        Object.entries(parameters).filter(([key]) => key === 'value' || key === 'currency'),
      );
  const canEnhance = adsMeasurementGranted && isSha256EmailAddress(sha256EmailAddress);
  let userDataWasSet = false;
  if (canEnhance) {
    try {
      // Google's manual enhanced-conversion contract requires user_data to be queued immediately
      // before the conversion. The raw email never enters the data layer.
      window.gtag('set', 'user_data', { sha256_email_address: sha256EmailAddress });
      userDataWasSet = true;
    } catch {
      // An enhancement failure must not suppress the underlying conversion.
    }
  }

  try {
    window.gtag('event', 'conversion', {
      send_to: sendTo,
      ...consentSafeParameters,
      event_callback: () => {
        // This callback means the Google tag finished processing the command (or reached its
        // callback timeout). It deliberately does not claim network receipt or attribution.
        trackEvent('google-ads-conversion-command', {
          goal,
          result: 'callback',
          tagLoadState: getGoogleTagLoadState(),
        });
      },
      event_timeout: GOOGLE_ADS_EVENT_CALLBACK_TIMEOUT_MS,
    });
    trackEvent('google-ads-conversion-command', {
      goal,
      result: 'queued',
      tagLoadState: getGoogleTagLoadState(),
    });
    return 'queued';
  } catch {
    const result =
      !adsMeasurementGranted && !isTemporaryAdvancedConsentModeActive()
        ? 'denied'
        : 'unavailable';
    trackEvent('google-ads-conversion-command', {
      goal,
      result,
      tagLoadState: getGoogleTagLoadState(),
    });
    return result;
  } finally {
    if (userDataWasSet) {
      try {
        // `set` is global to subsequent page events. Clear it immediately so an anonymous event,
        // logout, or account switch cannot inherit the previous user's identifier.
        window.gtag('set', 'user_data', null);
      } catch {
        // Conversion reporting is best-effort and must never affect the product flow.
      }
    }
  }
}

export function trackSignupConversion(
  options: EnhancedConversionOptions = {},
): Promise<ConversionTrackingResult> {
  return queueGoogleAdsConversion(
    'signup',
    'AW-17791132671/uc0WCJ_cxM4bEP_HvKNC',
    {},
    options,
  );
}

/**
 * Maps upload size to a small, bounded conversion-value proxy. Google Ads requires the proxy in
 * its value/currency fields, but raw byte counts must never leave the product as advertising data.
 */
export function getFileUploadConversionProxyValue(totalUploadSizeBytes: number): number | null {
  if (!Number.isSafeInteger(totalUploadSizeBytes) || totalUploadSizeBytes < 0) {
    return null;
  }
  if (totalUploadSizeBytes < FILE_UPLOAD_100_MIB) {
    return 1;
  }
  if (totalUploadSizeBytes < FILE_UPLOAD_500_MIB) {
    return 2;
  }
  if (totalUploadSizeBytes < FILE_UPLOAD_2_GIB) {
    return 3;
  }
  return 5;
}

export async function trackFileUploadConversion(
  uploadId: string,
  totalUploadSizeBytes: number,
): Promise<ConversionTrackingResult> {
  const transactionId = hasAdsMeasurementConsent()
    ? await hashGoogleAdsTransactionId(uploadId)
    : null;
  const proxyValue = getFileUploadConversionProxyValue(totalUploadSizeBytes);
  return queueGoogleAdsConversion(
    'file_upload',
    'AW-17791132671/hfEJCNGNxNAbEP_HvKNC',
    {
      ...(proxyValue === null ? {} : { value: proxyValue, currency: 'CHF' }),
      ...(transactionId ? { transaction_id: transactionId } : {}),
    },
  );
}

export function trackPwaInstallConversion(): Promise<ConversionTrackingResult> {
  return queueGoogleAdsConversion(
    'pwa_install',
    'AW-17791132671/t7iiCK6dgtIbEP_HvKNC',
  );
}

export function trackSubscriptionConversion(
  value: number,
  currency: string,
  transactionId?: string | null,
): Promise<ConversionTrackingResult> {
  const eventParameters: Record<string, string | number> = {
    value,
    currency,
  };
  if (transactionId?.trim()) {
    eventParameters.transaction_id = transactionId.trim();
  }
  return queueGoogleAdsConversion(
    'subscription',
    'AW-17791132671/FIdWCJWgzM4bEP_HvKNC',
    eventParameters,
  );
}
